import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import axios from 'axios';

const NAVY = [15, 43, 91];
const BLUE = [29, 78, 216];
const LIGHT_BG = [241, 245, 249];
const BORDER = [191, 219, 254];
const TEXT_DARK = [30, 41, 59];
const TEXT_MID = [71, 85, 105];
const TEXT_LIGHT = [148, 163, 184];

const RISK_COLORS = {
  'Unacceptable': [127, 29, 29],
  'High':         [146, 64, 14],
  'Limited':      [120, 53, 15],
  'Minimal':      [20, 83, 45],
  'Unclassified': [71, 85, 105]
};

function cleanName(name, maxLen = 30) {
  if (!name) return '--';
  return name.length > maxLen ? name.substring(0, maxLen - 3) + '...' : name;
}

function addPageHeader(doc, orgName, today) {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageWidth, 14, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.text(`${orgName} -- AI Governance Board Report  |  ${today}`, 14, 9.5);
}

function addPageNumbers(doc) {
  const totalPages = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...TEXT_LIGHT);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Page ${i} of ${totalPages}`,
      pageWidth - 14,
      pageHeight - 8,
      { align: 'right' }
    );
    doc.text('CONFIDENTIAL -- For Board Use Only', 14, pageHeight - 8);
  }
}

function addSectionTitle(doc, number, title, yPos) {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setTextColor(...NAVY);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(`${number}.  ${title}`, 14, yPos);
  doc.setDrawColor(...NAVY);
  doc.setLineWidth(0.5);
  doc.line(14, yPos + 3, pageWidth - 14, yPos + 3);
  return yPos + 10;
}

// ── FULL BOARD REPORT ─────────────────────────────────────
export async function generateBoardReport(tokenOrUser, inventoryOrUser, auditLogArg) {
  let token, user, inventory, auditLog, analysis, progress;

  // Backward compatibility: Determine if called from new Progress Tracker (token, user) or old Dashboard (user, inventory, audit)
  if (typeof tokenOrUser === 'string') {
    token = tokenOrUser;
    user = inventoryOrUser; 
    
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const headers = { Authorization: `Bearer ${token}` };

    // Fetch real-time data for the enterprise report
    const [invRes, riskRes, progRes, auditRes] = await Promise.all([
      axios.get(`${baseURL}/api/inventory`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${baseURL}/api/inventory/analyze`, { headers }).catch(() => ({ data: {} })),
      axios.get(`${baseURL}/api/progress`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${baseURL}/api/audit`, { headers }).catch(() => ({ data: [] }))
    ]);

    // THE FIX: Enforce array typing and extract tasks from the new Telemetry object structure
    inventory = Array.isArray(invRes.data) ? invRes.data : [];
    analysis = riskRes.data || {};
    progress = Array.isArray(progRes.data) ? progRes.data : (progRes.data?.tasks || []);
    auditLog = Array.isArray(auditRes.data) ? auditRes.data : [];
    
  } else {
    // Legacy fallback for other pages
    user = tokenOrUser;
    inventory = Array.isArray(inventoryOrUser) ? inventoryOrUser : [];
    auditLog = Array.isArray(auditLogArg) ? auditLogArg : [];
    analysis = {}; 
    progress = []; 
  }

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const today = new Date().toLocaleDateString('en-GB', {
    day: '2-digit', month: 'long', year: 'numeric'
  });
  const orgName = user?.org_name || user?.organisations?.name || 'Organisation';

  // Cover header
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageWidth, 48, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('AI GOVERNANCE', 14, 18);
  doc.text('BOARD REPORT', 14, 30);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(147, 197, 253);
  doc.text(`${orgName}  |  ${user?.vertical || ''}  |  ${today}`, 14, 40);
  doc.text('Powered by FCAIO Platform  --  Confidential', 14, 46);

  // Calculate Metrics
  const highRisk = inventory.filter(t => t.eu_ai_act_risk === 'High' || t.eu_ai_act_risk === 'Unacceptable').length;
  const shadowAI = inventory.filter(t => !t.sanctioned).length;
  const avgScore = inventory.length > 0 ? (inventory.reduce((s, t) => s + (t.risk_score || 0), 0) / inventory.length).toFixed(1) : 0;
  
  const totalTasks = progress.length;
  const completedTasks = progress.filter(t => t.status === 'Completed').length;
  const completionRate = totalTasks === 0 ? 0 : Math.round((completedTasks / totalTasks) * 100);

  let exposureText = 'Pending Analysis';
  if (analysis?.combined_exposure) {
    const minExp = analysis.combined_exposure.total_min?.toLocaleString() || '0';
    const maxExp = analysis.combined_exposure.total_max?.toLocaleString() || '0';
    exposureText = `${analysis.combined_exposure.currency || 'USD'} ${minExp} - ${maxExp}`;
  }

  // Executive Summary Box
  doc.setFillColor(...LIGHT_BG);
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.5);
  doc.roundedRect(14, 56, pageWidth - 28, 58, 3, 3, 'FD'); // Height increased to 58 to fit new rows
  doc.setTextColor(...NAVY);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('EXECUTIVE SUMMARY', 20, 65);

  const stats = [
    ['Total AI Tools', inventory.length],
    ['High / Unacceptable Risk', highRisk],
    ['Shadow AI Detected', shadowAI],
    ['Avg Risk Score', `${avgScore} / 10`],
    ['Compliance Completion', `${completionRate}%`],
    ['Est. Financial Exposure', exposureText]
  ];

  stats.forEach((stat, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const x = col === 0 ? 20 : 115;
    const y = 74 + row * 14;
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...TEXT_MID);
    doc.text(stat[0], x, y);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...NAVY);
    doc.text(String(stat[1]), x, y + 8);
  });

  // Frameworks bar
  doc.setFillColor(219, 234, 254);
  doc.roundedRect(14, 120, pageWidth - 28, 12, 2, 2, 'F');
  doc.setTextColor(...BLUE);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text(
    'Frameworks: EU AI Act (2024/1689)  |  NIST AI RMF (2023)  |  GDPR (2016/679)  |  ISO/IEC 42001:2023',
    pageWidth / 2, 127, { align: 'center' }
  );

  // Section 1: Inventory
  let y = addSectionTitle(doc, 1, 'AI INVENTORY REGISTER', 140);

  autoTable(doc, {
    startY: y,
    head: [['Tool', 'Vendor', 'Dept', 'AI Risk Level', 'Score', 'Regulations', 'Status']],
    body: inventory.map(t => [
      cleanName(t.tool_name, 25),
      cleanName(t.vendor, 20) || '--',
      t.department || '--',
      t.eu_ai_act_risk,
      `${t.risk_score || '--'}/10`,
      (t.applicable_regulations || []).join(', ') || '--',
      t.sanctioned ? 'Sanctioned' : 'Shadow AI'
    ]),
    headStyles: { fillColor: BLUE, textColor: 255, fontStyle: 'bold', fontSize: 9 },
    bodyStyles: { fontSize: 8.5, textColor: TEXT_DARK },
    alternateRowStyles: { fillColor: LIGHT_BG },
    columnStyles: {
      0: { cellWidth: 30 },
      1: { cellWidth: 28 },
      2: { cellWidth: 20 },
      3: { cellWidth: 26, fontStyle: 'bold' },
      4: { cellWidth: 16, halign: 'center' },
      5: { cellWidth: 30 },
      6: { cellWidth: 24 }
    },
    margin: { left: 14, right: 14 }
  });

  y = doc.lastAutoTable.finalY + 15;

  // 🌟 NEW SECTION 2: Compliance Progress & Evidence Vault 🌟
  if (y > 240) { doc.addPage(); addPageHeader(doc, orgName, today); y = 22; }
  y = addSectionTitle(doc, 2, 'COMPLIANCE PROGRESS & EVIDENCE VAULT', y);

  const progressBody = progress.map(task => [
    cleanName(task.tool_name, 25),
    cleanName(task.task_title || task.item_title, 45), // Safer extraction
    task.status,
    task.evidence_file_name || task.evidence ? `Yes (Vaulted)` : 'None'
  ]);

  autoTable(doc, {
    startY: y,
    head: [['Target System', 'Mitigation Action', 'Status', 'Evidence Status']],
    body: progressBody.length > 0 ? progressBody : [['No active compliance tasks tracked', '-', '-', '-']],
    headStyles: { fillColor: BLUE, textColor: 255, fontStyle: 'bold', fontSize: 9 },
    bodyStyles: { fontSize: 8.5, textColor: TEXT_DARK },
    alternateRowStyles: { fillColor: LIGHT_BG },
    margin: { left: 14, right: 14 }
  });

  y = doc.lastAutoTable.finalY + 15;

  // Section 3: Board Summaries (Shifted from Section 2)
  doc.addPage();
  addPageHeader(doc, orgName, today);
  y = addSectionTitle(doc, 3, 'GEMINI AI -- BOARD SUMMARIES & GOVERNANCE CONTROLS', 26);

  inventory.filter(t => t.board_summary).forEach((tool, index) => {
    const summaryLines = doc.splitTextToSize(tool.board_summary || '', pageWidth - 36);
    const controlsHeight = (tool.top_controls || []).reduce((total, ctrl) => {
      return total + doc.splitTextToSize(`-  ${ctrl}`, pageWidth - 44).length * 5;
    }, 0);
    const blockHeight = 14 + summaryLines.length * 5 + 16 + controlsHeight + 14;

    if (y + blockHeight > 270) {
      doc.addPage();
      addPageHeader(doc, orgName, today);
      y = 22;
    }

    // Tool header
    doc.setFillColor(...LIGHT_BG);
    doc.roundedRect(14, y, pageWidth - 28, 11, 2, 2, 'F');
    doc.setTextColor(...NAVY);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    const displayName = cleanName(tool.tool_name, 35);
    doc.text(`${displayName}  (${tool.vendor || 'Unknown'})`, 18, y + 7.5);

    // Risk badge
    const riskColor = RISK_COLORS[tool.eu_ai_act_risk] || RISK_COLORS['Unclassified'];
    doc.setFillColor(...riskColor);
    doc.roundedRect(pageWidth - 52, y + 2, 38, 7, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(tool.eu_ai_act_risk, pageWidth - 33, y + 7, { align: 'center' });
    y += 14;

    // Risk score line
    doc.setTextColor(...TEXT_MID);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Risk Score: ${tool.risk_score}/10  |  Regulations: ${(tool.applicable_regulations || []).join(', ') || 'None'}  |  ${tool.sanctioned ? 'Sanctioned' : 'Shadow AI'}`,
      18, y
    );
    y += 6;

    // Summary
    doc.setTextColor(...TEXT_DARK);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(summaryLines, 18, y);
    y += summaryLines.length * 5 + 4;

    // Controls
    if (tool.top_controls && tool.top_controls.length > 0) {
      doc.setFillColor(219, 234, 254);
      doc.roundedRect(18, y, pageWidth - 36, 7, 2, 2, 'F');
      doc.setTextColor(...BLUE);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('GOVERNANCE CONTROLS REQUIRED:', 22, y + 5);
      y += 10;

      tool.top_controls.forEach(control => {
        doc.setTextColor(...TEXT_DARK);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        const controlLines = doc.splitTextToSize(`-  ${control}`, pageWidth - 44);
        doc.text(controlLines, 24, y);
        y += controlLines.length * 5 + 2;
      });
    }

    y += 8;

    if (index < inventory.filter(t => t.board_summary).length - 1) {
      doc.setDrawColor(...BORDER);
      doc.setLineWidth(0.3);
      doc.line(14, y, pageWidth - 14, y);
      y += 6;
    }
  });

  // Section 4: Audit Trail (Shifted from Section 3)
  doc.addPage();
  addPageHeader(doc, orgName, today);
  y = addSectionTitle(doc, 4, 'AUDIT TRAIL -- GOVERNANCE ACTIVITY LOG', 26);

  const auditCount = auditLog.length;
  doc.setTextColor(...TEXT_MID);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.text(
    `${auditCount} governance events recorded. Showing most recent 50.${auditCount < 3 ? ' Note: Earlier events pre-date audit logging activation.' : ''}`,
    14, y
  );
  y += 6;

  autoTable(doc, {
    startY: y,
    head: [['Date', 'Time', 'Action', 'Tool / Entity', 'Details']],
    body: auditLog.slice(0, 50).map(log => {
      let details = '--';
      if (log.action === 'GEMINI_ANALYSIS_COMPLETED') {
        details = `Risk: ${log.details?.eu_ai_act_risk || '--'}, Score: ${log.details?.risk_score || '--'}/10, Regs: ${(log.details?.regulations || []).join(', ') || '--'}`;
      } else if (log.action === 'TOOL_ADDED') {
        details = `Vendor: ${log.details?.vendor || '--'} | Dept: ${log.details?.department || '--'} | ${log.details?.sanctioned ? 'Sanctioned' : 'Shadow AI'}`;
      } else if (log.action === 'TOOL_REMOVED') {
        details = `Vendor: ${log.details?.vendor || '--'} removed from inventory`;
      } else if (log.action === 'TASK_COMPLETED_WITH_EVIDENCE') {
        details = `Evidence: ${log.details?.file_name || 'Vaulted'}`;
      }
      return [
        new Date(log.created_at).toLocaleDateString('en-GB', {
          day: '2-digit', month: 'short', year: 'numeric'
        }),
        new Date(log.created_at).toLocaleTimeString('en-GB', {
          hour: '2-digit', minute: '2-digit'
        }),
        log.action.replace(/_/g, ' '),
        cleanName(log.entity_name || '--', 20),
        details
      ];
    }),
    headStyles: { fillColor: BLUE, textColor: 255, fontStyle: 'bold', fontSize: 9 },
    bodyStyles: { fontSize: 8, textColor: TEXT_DARK },
    alternateRowStyles: { fillColor: LIGHT_BG },
    columnStyles: {
      0: { cellWidth: 22 },
      1: { cellWidth: 14 },
      2: { cellWidth: 40 },
      3: { cellWidth: 30 },
      4: { cellWidth: 80 }
    },
    margin: { left: 14, right: 14 }
  });

  // Disclaimer
  const lastTableY = doc.lastAutoTable?.finalY || 200;
  const finalY = lastTableY + 10;

  const disclaimerLines1 = doc.splitTextToSize(
    'This report was generated by FCAIO Platform using Gemini AI, cross-referenced against EU AI Act (2024/1689), NIST AI RMF (2023), GDPR (2016/679), and ISO/IEC 42001:2023. Classifications are indicative. Board presentation review by a qualified Fractional CAIO is recommended before formal submission.',
    pageWidth - 44
  );
  const boxHeight = 10 + disclaimerLines1.length * 5 + 6;

  doc.setFillColor(...LIGHT_BG);
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.5);
  doc.roundedRect(14, finalY, pageWidth - 28, boxHeight, 3, 3, 'FD');
  doc.setTextColor(...NAVY);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text('DISCLAIMER & METHODOLOGY', 20, finalY + 7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...TEXT_MID);
  doc.text(disclaimerLines1, 20, finalY + 13);

  addPageNumbers(doc);

  const filename = `AI_Governance_Report_${orgName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(filename);
}

// ── INDIVIDUAL TOOL REPORT ────────────────────────────────
export function generateToolReport(user, tool, auditLog) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const today = new Date().toLocaleDateString('en-GB', {
    day: '2-digit', month: 'long', year: 'numeric'
  });
  const orgName = user?.org_name || 'Organisation';
  const riskColor = RISK_COLORS[tool.eu_ai_act_risk] || RISK_COLORS['Unclassified'];

  // Header
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageWidth, 44, 'F');
  doc.setTextColor(147, 197, 253);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('AI TOOL GOVERNANCE ASSESSMENT', 14, 10);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(cleanName(tool.tool_name, 35), 14, 24);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(147, 197, 253);
  doc.text(`${orgName}  |  ${user?.vertical || ''}  |  Generated: ${today}`, 14, 34);
  doc.text(`Vendor: ${tool.vendor || 'Unknown'}  |  Department: ${tool.department || 'Not specified'}`, 14, 41);

  // Risk badge
  doc.setFillColor(...riskColor);
  doc.roundedRect(pageWidth - 58, 12, 44, 16, 3, 3, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('AI RISK LEVEL', pageWidth - 36, 19, { align: 'center' });
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(tool.eu_ai_act_risk, pageWidth - 36, 26, { align: 'center' });

  // Details grid
  doc.setFillColor(...LIGHT_BG);
  doc.setDrawColor(...BORDER);
  doc.roundedRect(14, 52, pageWidth - 28, 38, 3, 3, 'FD');

  const details = [
    ['Vendor', cleanName(tool.vendor, 30) || '--'],
    ['Department', tool.department || '--'],
    ['Risk Score', `${tool.risk_score || '--'} / 10`],
    ['AI Risk Level', tool.eu_ai_act_risk],
    ['Sanctioned', tool.sanctioned ? 'Yes - Approved by IT' : 'No - Shadow AI'],
    ['Regulations', (tool.applicable_regulations || []).join(', ') || '--'],
    ['Data Processed', (tool.data_types || []).join(', ') || '--'],
    ['Analysed By', 'Gemini AI via FCAIO Platform']
  ];

  details.forEach((detail, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const x = col === 0 ? 20 : 115;
    const y = 61 + row * 10;
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...TEXT_MID);
    doc.text(detail[0].toUpperCase(), x, y);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...TEXT_DARK);
    doc.text(detail[1], x, y + 5);
  });

  // Section 1: Board Summary
  let y = 100;
  y = addSectionTitle(doc, 1, 'BOARD SUMMARY', y);

  doc.setFillColor(239, 246, 255);
  doc.setDrawColor(...BORDER);
  const summaryLines = doc.splitTextToSize(
    tool.board_summary || 'No summary available.',
    pageWidth - 40
  );
  const summaryHeight = summaryLines.length * 5.5 + 10;
  doc.roundedRect(14, y, pageWidth - 28, summaryHeight, 3, 3, 'FD');
  doc.setTextColor(...TEXT_DARK);
  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'normal');
  doc.text(summaryLines, 20, y + 7);
  y += summaryHeight + 10;

  // Section 2: Governance Controls
  y = addSectionTitle(doc, 2, 'GOVERNANCE CONTROLS REQUIRED', y);

  if (tool.top_controls && tool.top_controls.length > 0) {
    tool.top_controls.forEach((control, i) => {
      const controlLines = doc.splitTextToSize(control, pageWidth - 50);
      const controlHeight = controlLines.length * 5.5 + 12;

      doc.setFillColor(i % 2 === 0 ? 239 : 248, i % 2 === 0 ? 246 : 250, 255);
      doc.setDrawColor(...BORDER);
      doc.roundedRect(14, y, pageWidth - 28, controlHeight, 2, 2, 'FD');

      doc.setFillColor(...BLUE);
      doc.circle(24, y + controlHeight / 2, 5, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text(String(i + 1), 24, y + controlHeight / 2 + 2.5, { align: 'center' });

      doc.setTextColor(...TEXT_DARK);
      doc.setFontSize(9.5);
      doc.setFont('helvetica', 'normal');
      doc.text(controlLines, 34, y + 7);
      y += controlHeight + 4;
    });
  }

  y += 6;

  // Section 3: Applicable Regulations
  if (y + 60 > doc.internal.pageSize.getHeight() - 20) {
    doc.addPage();
    addPageHeader(doc, orgName, today);
    y = 22;
  }
  y = addSectionTitle(doc, 3, 'APPLICABLE REGULATIONS', y);

  const regDetails = {
    'GDPR':    'Article 28 — Data Processing Agreement required for all third-party processors handling personal data.',
    'HIPAA':   'Section 164.308 — Business Associate Agreement required for vendors handling Protected Health Information.',
    'FERPA':   '20 U.S.C. 1232g — AI tools processing student education records must comply with consent requirements.',
    'DORA':    'Article 28 — ICT third-party risk management requirements for financial entities.',
    'CCPA':    'Cal. Civ. Code 1798.100 — Consumer rights over personal data processing.',
    'MAS TRM': 'Guidelines on technology risk management for financial institutions in Singapore.',
  };

 const regsToShow = tool.applicable_regulations || [];
  if (regsToShow.length === 0) {
    doc.setTextColor(...TEXT_MID);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('No specific regulations identified for this tool.', 20, y);
    y += 10;
  }
  regsToShow.forEach((reg) => {
    if (y + 20 > doc.internal.pageSize.getHeight() - 20) {
      doc.addPage();
      addPageHeader(doc, orgName, today);
      y = 22;
    }
    doc.setFillColor(...LIGHT_BG);
    doc.setDrawColor(...BORDER);
    doc.roundedRect(14, y, pageWidth - 28, 16, 2, 2, 'FD');
    doc.setTextColor(...NAVY);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(reg, 20, y + 6);
    doc.setTextColor(...TEXT_MID);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    const regLine = doc.splitTextToSize(
      regDetails[reg] || `${reg} compliance required.`,
      pageWidth - 46
    );
    doc.text(regLine[0], 20, y + 12);
    y += 20;
  });

  y += 6;

  // Section 4: Audit Trail for this tool
  const toolAudit = auditLog.filter(log => log.entity_name === tool.tool_name);

  if (toolAudit.length > 0) {
    doc.addPage();
    addPageHeader(doc, orgName, today);
    y = 22;
    y = addSectionTitle(doc, 4, `AUDIT TRAIL -- ${cleanName(tool.tool_name, 35)}`, y);

    autoTable(doc, {
      startY: y,
      head: [['Date', 'Time', 'Action', 'Details']],
      body: toolAudit.map(log => [
        new Date(log.created_at).toLocaleDateString('en-GB', {
          day: '2-digit', month: 'short', year: 'numeric'
        }),
        new Date(log.created_at).toLocaleTimeString('en-GB', {
          hour: '2-digit', minute: '2-digit'
        }),
        log.action.replace(/_/g, ' '),
        log.action === 'GEMINI_ANALYSIS_COMPLETED'
          ? `Risk: ${log.details?.eu_ai_act_risk}, Score: ${log.details?.risk_score}/10`
          : log.action === 'TOOL_ADDED'
          ? `Vendor: ${log.details?.vendor || '--'} | ${log.details?.sanctioned ? 'Sanctioned' : 'Shadow AI'}`
          : '--'
      ]),
      headStyles: { fillColor: BLUE, textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 8.5, textColor: TEXT_DARK },
      alternateRowStyles: { fillColor: LIGHT_BG },
      margin: { left: 14, right: 14 }
    });

    y = doc.lastAutoTable?.finalY || y;
  }

  // Disclaimer
  const lastY = doc.lastAutoTable?.finalY || y;
  let finalY = Math.max(lastY, y) + 10;

  if (finalY + 28 > doc.internal.pageSize.getHeight() - 10) {
    doc.addPage();
    addPageHeader(doc, orgName, today);
    finalY = 24;
  }

  const toolDisclaimerLines = doc.splitTextToSize(
    'Generated by FCAIO Platform using Gemini AI. Cross-referenced against EU AI Act (2024/1689), NIST AI RMF (2023), and GDPR (2016/679). Classifications are indicative. Review by a qualified Fractional CAIO is recommended before board presentation.',
    pageWidth - 44
  );
  const toolBoxHeight = 10 + toolDisclaimerLines.length * 5 + 6;

  doc.setFillColor(...LIGHT_BG);
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.5);
  doc.roundedRect(14, finalY, pageWidth - 28, toolBoxHeight, 3, 3, 'FD');
  doc.setTextColor(...NAVY);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text('DISCLAIMER', 20, finalY + 7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...TEXT_MID);
  doc.text(toolDisclaimerLines, 20, finalY + 13);

  addPageNumbers(doc);

  const filename = `Tool_Report_${tool.tool_name.replace(/[^a-zA-Z0-9]/g, '_')}_${orgName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(filename);
}